import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { ErrorFallback } from '@/ErrorFallback';
import { OfflineBanner } from '@/components/OfflineBanner';
import { Layout } from '@/components/layout/Layout';
import { PageMeta } from '@/components/PageMeta';
import { HomePage } from '@/pages/HomePage';
import { trackPageView } from '@/lib/analytics';
import { useI18n } from '@/lib/i18n';

// Route-level loading keeps the initial shell small and lets the browser fetch
// only the page surface needed for the current URL.
// The home hero is the first public surface and the LCP element; keep it in the
// initial route bundle while secondary and protected routes remain lazy.
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const Toaster = lazy(() => import('@/components/ui/sonner').then((m) => ({ default: m.Toaster })));
const InstallPrompt = lazy(() => import('@/components/InstallPrompt').then((m) => ({ default: m.InstallPrompt })));

// Lazily loaded (split into separate chunks)
const AboutPage = lazy(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PartnerPortalPage = lazy(() =>
  import('@/pages/PartnerPortalPage').then((m) => ({ default: m.PartnerPortalPage })),
);
const PartnerOrganizationPage = lazy(() =>
  import('@/pages/PartnerOrganizationPage').then((m) => ({ default: m.PartnerOrganizationPage })),
);
const StateListPage = lazy(() => import('@/pages/StateListPage').then((m) => ({ default: m.StateListPage })));
const CountyListPage = lazy(() => import('@/pages/CountyListPage').then((m) => ({ default: m.CountyListPage })));
const TownCenterPage = lazy(() => import('@/pages/TownCenterPage').then((m) => ({ default: m.TownCenterPage })));
const ConnectingHandsPage = lazy(() =>
  import('@/pages/ConnectingHandsPage').then((m) => ({ default: m.ConnectingHandsPage })),
);
const FoodHelpPage = lazy(() => import('@/pages/FoodHelpPage').then((m) => ({ default: m.FoodHelpPage })));
const CountyVerificationPage = lazy(() =>
  import('@/pages/CountyVerificationPage').then((m) => ({ default: m.CountyVerificationPage })),
);
const DataAdminPage = lazy(() => import('@/pages/DataAdminPage').then((m) => ({ default: m.DataAdminPage })));
const CommunityPortalPage = lazy(() =>
  import('@/pages/CommunityPortalPage').then((m) => ({ default: m.CommunityPortalPage })),
);
const CountySupportPage = lazy(() =>
  import('@/pages/CountySupportPage').then((m) => ({ default: m.CountySupportPage })),
);
const AccountPortalPage = lazy(() =>
  import('@/pages/AccountPortalPage').then((m) => ({ default: m.AccountPortalPage })),
);
const TrustPage = lazy(() => import('@/pages/TrustPage').then((m) => ({ default: m.TrustPage })));

const MemberRoute = lazy(() => import('@/components/ProtectedRoute').then((m) => ({ default: m.MemberRoute })));
const AdminRoute = lazy(() => import('@/components/ProtectedRoute').then((m) => ({ default: m.AdminRoute })));
const SuperAdminRoute = lazy(() => import('@/components/ProtectedRoute').then((m) => ({ default: m.SuperAdminRoute })));

function PageLoader() {
  const { t } = useI18n();

  return (
    <main aria-busy="true" className="min-h-screen bg-[#f4efe4] flex items-center justify-center px-6">
      <div className="relative overflow-hidden rounded-[2rem] border border-[#173f31]/10 bg-[#fffaf0]/88 p-8 text-center shadow-[0_24px_70px_rgba(43,51,39,0.14)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(185,85,52,0.12),transparent_34%),linear-gradient(135deg,rgba(23,63,49,0.08),transparent_48%)]" />
        <div className="relative mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-[#173f31] text-[#fff8e7] shadow-[0_16px_34px_rgba(23,63,49,0.24)]">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#fff8e7]/30 border-t-[#fff8e7]" />
        </div>
        <h1 className="relative font-serif text-2xl font-black tracking-[-0.04em] text-[#15231d]">
          {t('utility.loadingLocalSupport')}
        </h1>
        <p className="relative mt-1 text-xs font-black uppercase tracking-[0.22em] text-[#7b827b]">
          {t('utility.verifiedAidNetwork')}
        </p>
      </div>
    </main>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    const requestIdle = (window as Window & { requestIdleCallback?: typeof requestIdleCallback }).requestIdleCallback;
    const sendPageView = () => {
      void trackPageView(path, document.title);
    };

    // Page analytics must not compete with the first public render or LCP.
    if (requestIdle) {
      const idleId = requestIdle(sendPageView, { timeout: 2000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(sendPageView, 1200);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  return null;
}

function PrivatePageMeta({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <>
      <PageMeta title={title} description={description} noIndex />
      {children}
    </>
  );
}

function App() {
  const { t } = useI18n();

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthProvider>
        <BrowserRouter>
          <OfflineBanner />
          <Suspense fallback={null}>
            <Toaster />
            <InstallPrompt />
          </Suspense>
          <AnalyticsRouteTracker />
          <a
            href="#main-content"
            className="fixed left-4 top-2 z-[100] -translate-y-20 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg transition-transform focus:translate-y-0"
          >
            {t('utility.skipToMain')}
          </a>
          <Layout>
            <div id="main-content" tabIndex={-1} className="outline-none">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy" element={<TrustPage kind="privacy" />} />
                  <Route path="/terms" element={<TrustPage kind="terms" />} />
                  <Route path="/faq" element={<TrustPage kind="faq" />} />
                  <Route path="/accessibility" element={<TrustPage kind="accessibility" />} />

                  {/* Member routes - require login */}
                  <Route
                    path="/dashboard"
                    element={
                      <PrivatePageMeta
                        title="Member Dashboard | Planet Earth Help"
                        description="Manage saved locations, alerts, and community activity in your Planet Earth Help account."
                      >
                        <MemberRoute>
                          <DashboardPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  {/* Public routes - but require login to interact */}
                  <Route path="/connecting-hands" element={<ConnectingHandsPage />} />
                  <Route path="/food" element={<FoodHelpPage />} />

                  {/* Admin routes */}
                  <Route
                    path="/county-verification"
                    element={
                      <PrivatePageMeta
                        title="County Verification | Planet Earth Help"
                        description="Review internal county coverage and geographic verification status."
                      >
                        <AdminRoute>
                          <CountyVerificationPage />
                        </AdminRoute>
                      </PrivatePageMeta>
                    }
                  />
                  <Route
                    path="/data-admin"
                    element={
                      <PrivatePageMeta
                        title="Data Administration | Planet Earth Help"
                        description="Internal resource data operations and release controls."
                      >
                        <SuperAdminRoute>
                          <DataAdminPage />
                        </SuperAdminRoute>
                      </PrivatePageMeta>
                    }
                  />

                  <Route
                    path="/community"
                    element={
                      <PrivatePageMeta
                        title="Community Portal | Planet Earth Help"
                        description="Participate in moderated county-based community updates and connections."
                      >
                        <MemberRoute>
                          <CommunityPortalPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  <Route
                    path="/:state/:county/community"
                    element={
                      <PrivatePageMeta
                        title="County Community Portal | Planet Earth Help"
                        description="Participate in moderated updates connected to this county Town Center."
                      >
                        <MemberRoute>
                          <CommunityPortalPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  <Route
                    path="/partner-portal"
                    element={
                      <PrivatePageMeta
                        title="Partner Workspace | Planet Earth Help"
                        description="Manage an approved community organization profile and county-linked Town Center pages."
                      >
                        <MemberRoute>
                          <PartnerPortalPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  <Route path="/organization/:organizationId" element={<PartnerOrganizationPage />} />

                  <Route
                    path="/account"
                    element={
                      <PrivatePageMeta
                        title="Account | Planet Earth Help"
                        description="Manage your Planet Earth Help profile, privacy, and saved resource lists."
                      >
                        <MemberRoute>
                          <AccountPortalPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  <Route
                    path="/:state/:county/support"
                    element={
                      <PrivatePageMeta
                        title="County Support Workspace | Planet Earth Help"
                        description="Manage county-scoped support workflows and approved community information."
                      >
                        <MemberRoute>
                          <CountySupportPage />
                        </MemberRoute>
                      </PrivatePageMeta>
                    }
                  />

                  {/* Public routes - browsing */}
                  <Route path="/:state" element={<StateListPage />} />
                  <Route path="/:state/:county" element={<CountyListPage />} />
                  <Route path="/:state/:county/:citySlug" element={<TownCenterPage />} />

                  {/* 404 catch-all */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </div>
          </Layout>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
